import React, { useEffect, useState } from 'react';
import API from '../api';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../hooks/useTranslation';
import TablePagination, { DEFAULT_PAGE_SIZE, paginateItems } from './TablePagination';

export default function OBUManagement() {
  const { t } = useTranslation();
  const [obuDevices, setObuDevices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [formData, setFormData] = useState({
    device_id: '',
    device_name: '',
    device_type: 'Standard card',
    balance: 0,
    deposit_amount: 0,
    created_at: '',
    warranty_expiry: '',
    firmware_version: '',
    hardware_version: '',
    manufacturer: '',
    installation_date: '',
    location: '',
    coordinates: '',
    status: 'active',
    battery_level: '',
    signal_strength: '',
    communication_protocol: 'DSRC',
    frequency_band: '5.9GHz',
    transmission_power: '',
    encryption_key: '',
    certificate_id: '',
    vehicle_id: '',
    owner_contact: '',
    maintenance_schedule: '',
    last_maintenance: '',
    notes: ''
  });

  const deviceStatuses = [
    { value: 'active', label: '🟢 Active', color: 'bg-green-100 text-green-800' },
    { value: 'assigned', label: '📍 Assigned', color: 'bg-blue-100 text-blue-800' },
    { value: 'in_service', label: '🚀 In Service', color: 'bg-purple-100 text-purple-800' },
    // { value: 'inactive', label: '🔴 Inactive', color: 'bg-red-100 text-red-800' },
    // { value: 'maintenance', label: '⏸️ Maintenance', color: 'bg-yellow-100 text-yellow-800' },
    // { value: 'faulty', label: '🔴 Faulty', color: 'bg-red-100 text-red-800' },
    { value: 'return', label: '↩️ Returned', color: 'bg-orange-100 text-orange-800' }
  ];

  const deviceTypes = ['Standard card', 'VIP card'];
  // const protocols = ['DSRC', 'C-V2X', 'WiFi', 'Bluetooth', '4G/5G']; // unused
  // const frequencies = ['5.9GHz', '2.4GHz', '5.8GHz', '700MHz', '3.5GHz']; // unused

  const fetchOBUDevices = React.useCallback(async () => {
    try {
      const response = await API.get('/admin/obu-devices');
      console.log('📊 Fetched OBU devices:', response.data);
      // Log the first device to see its structure
      if (response.data.length > 0) {
        console.log('Sample OBU device:', response.data[0]);
        console.log('Status values:', response.data.map(d => ({ id: d.device_id, status: d.status })));
      }
      setObuDevices(response.data);
      setLoading(false);
    } catch (error) {
      toast.error(`❌ ${t('error_messages.load_obu_devices_failed')}`);
      console.error('Fetch OBU devices error:', error);
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchOBUDevices();
  }, [fetchOBUDevices]);

  const resetForm = () => {
    setFormData({
      device_id: '',
      device_name: '',
      device_type: 'Standard card',
      balance: 0,
      deposit_amount: 0,
      created_at: '',
      warranty_expiry: '',
      firmware_version: '',
      hardware_version: '',
      manufacturer: '',
      installation_date: '',
      location: '',
      coordinates: '',
      status: 'active',
      battery_level: '',
      signal_strength: '',
      communication_protocol: 'DSRC',
      frequency_band: '5.9GHz',
      transmission_power: '',
      encryption_key: '',
      certificate_id: '',
      vehicle_id: '',
      owner_contact: '',
      maintenance_schedule: '',
      last_maintenance: '',
      notes: ''
    });
    setEditingDevice(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingDevice) {
        // Update existing device
        console.log('🔄 Submitting OBU device update:', {
          id: editingDevice.id,
          formData: formData,
          status: formData.status
        });
        const response = await API.put(`/admin/obu-devices/${editingDevice.id}`, formData);
        console.log('✅ OBU device update response:', response.data);
        setObuDevices(obuDevices.map(device =>
          device.id === editingDevice.id ? response.data : device
        ));
        toast.success(`✅ ${t('obu.messages.update_success')}`);
      } else {
        // Add new device
        const response = await API.post('/admin/obu-devices', formData);
        setObuDevices([response.data, ...obuDevices]);
        toast.success(`✅ ${t('obu.messages.add_success')}`);
      }

      resetForm();
      setShowAddForm(false);
    } catch (error) {
      console.error('❌ OBU device update error:', error);
      toast.error(`❌ ${t('obu.messages.update_failed', { action: editingDevice ? t('obu.update_device') : t('obu.add_device'), message: error.response?.data?.message || error.message })}`);
    }
  };

  const handleEdit = (device) => {
    console.log('✏️ Editing OBU device:', device);
    console.log('Device status:', device.status);
    setEditingDevice(device);
    const editFormData = { ...device };
    console.log('Form data after spread:', editFormData);
    console.log('Form data status:', editFormData.status);
    setFormData(editFormData);
    setShowAddForm(true);
  };

  const handleDelete = async (id, deviceId) => {
    if (window.confirm(t('obu.messages.delete_confirm', { deviceId }))) {
      try {
        await API.delete(`/admin/obu-devices/${id}`);
        setObuDevices(obuDevices.filter(device => device.id !== id));
        toast.success(`✅ ${t('obu.messages.delete_success', { deviceId })}`);
      } catch (error) {
        toast.error(`❌ ${t('obu.messages.delete_failed')}`);
      }
    }
  };

  const filteredDevices = obuDevices.filter(device =>
    device.device_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.device_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.location?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    device.serial_number?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, pageSize]);

  const {
    pageItems: paginatedDevices,
    currentPage: safePage
  } = paginateItems(filteredDevices, currentPage, pageSize);

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
          📡 {t('obu.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          {t('obu.description')}
        </p>
      </div>

      {/* Header Actions */}
      <div className="mb-6 flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex-1">
          <input
            type="text"
            placeholder={`🔍 ${t('obu.search_placeholder')}`}
            className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="flex gap-2">
          <button
            onClick={() => {
              resetForm();
              setShowAddForm(!showAddForm);
            }}
            className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            ➡️ {t('obu.add_device')}
          </button>

          <button
            onClick={fetchOBUDevices}
            className="bg-blue-500 hover:bg-blue-600 text-white py-2 px-4 rounded-lg transition-colors duration-200"
          >
            🔄 {t('obu.refresh')}
          </button>
        </div>
      </div>

      {/* Add/Edit Device Form */}
      {showAddForm && (
        <div className="mb-6 p-6 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700">
          <h3 className="text-lg font-bold mb-4 text-gray-800 dark:text-white">
            {editingDevice ? `✏️ ${t('obu.edit_device')}` : `➡️ ${t('obu.add_new_device')}`}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Device Information */}
            <div className="border-b border-gray-300 dark:border-gray-600 pb-4">
              <h4 className="text-md font-semibold mb-3 text-gray-700 dark:text-gray-300">{t('obu.form_sections.basic_info')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Device ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={t('obu.placeholders.device_id_example')}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                    value={formData.device_id}
                    onChange={(e) => setFormData({ ...formData, device_id: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('obu.form_labels.device_name_required')}
                  </label>
                  <input
                    type="text"
                    required
                    placeholder={t('obu.placeholders.device_name_example')}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                    value={formData.device_name}
                    onChange={(e) => setFormData({ ...formData, device_name: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('obu.form_labels.device_type_required')}
                  </label>
                  <select
                    required
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                    value={formData.device_type}
                    onChange={(e) => setFormData({ ...formData, device_type: e.target.value })}
                  >
                    {deviceTypes.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    📅 Created Date
                  </label>
                  <input
                    type="text"
                    readOnly
                    placeholder="Auto-generated"
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-500 dark:text-white cursor-not-allowed"
                    value={formData.created_at ? new Date(formData.created_at).toLocaleDateString('en-GB') : (editingDevice ? '' : 'Will be set automatically')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    📅 Expiry Date <span className="text-xs text-blue-500 ml-1">(VIP)</span>
                  </label>
                  <input
                    type="date"
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                    value={formData.warranty_expiry ? formData.warranty_expiry.split('T')[0] : ''}
                    onChange={(e) => setFormData({ ...formData, warranty_expiry: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('obu.form_labels.status_required')}
                  </label>
                  <select
                    required
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  >
                    {deviceStatuses.map(status => (
                      <option key={status.value} value={status.value}>{status.label}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('obu.balance')}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder={t('obu.placeholders.balance_placeholder')}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-500 dark:text-white cursor-not-allowed"
                    value={formData.balance}
                    readOnly
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    💰 Deposit Amount
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="50000"
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded bg-gray-100 dark:bg-gray-500 dark:text-white cursor-not-allowed"
                    value={formData.deposit_amount}
                    readOnly
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    {t('obu.form_labels.firmware_version')}
                  </label>
                  <input
                    type="text"
                    placeholder={t('obu.placeholders.firmware_version_example')}
                    className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                    value={formData.firmware_version}
                    onChange={(e) => setFormData({ ...formData, firmware_version: e.target.value })}
                  />
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('obu.form_labels.notes')}
              </label>
              <textarea
                rows="4"
                placeholder={t('obu.placeholders.notes_example')}
                className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded focus:ring-2 focus:ring-green-500 dark:bg-gray-600 dark:text-white"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              />
            </div>

            <div className="flex gap-2 pt-4">
              <button
                type="submit"
                className="bg-green-500 hover:bg-green-600 text-white py-2 px-4 rounded transition-colors duration-200"
              >
                {editingDevice ? `✅ ${t('obu.update_device')}` : `✅ ${t('obu.add_device')}`}
              </button>
              <button
                type="button"
                onClick={() => {
                  resetForm();
                  setShowAddForm(false);
                }}
                className="bg-gray-500 hover:bg-gray-600 text-white py-2 px-4 rounded transition-colors duration-200"
              >
                ❌ {t('obu.cancel')}
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6"></div>
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-blue-100 dark:bg-blue-900 p-4 rounded-lg">
          <div className="text-2xl font-bold text-blue-800 dark:text-blue-200">
            {obuDevices.length}
          </div>
          <div className="text-blue-600 dark:text-blue-400">📡 Total Devices</div>
        </div>
        {/* device statuses */}
        {deviceStatuses.map(status => (
          <div key={status.value} className={`${status.color} p-4 rounded-lg`}>
            <div className="text-2xl font-bold">
              {obuDevices.filter(device => device.status === status.value).length}
            </div>
            <div className="text-sm">{status.label}</div>
          </div>
        ))}
      </div>


      {/* Devices Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
          <thead>
            <tr className="bg-gray-50 dark:bg-gray-700">
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Device ID</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">Serial Number</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">📍 Location</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-left">🚗 License Plate</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('obu.table_headers.type')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('obu.table_headers.balance')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">💰 Deposit</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('obu.table_headers.status')}</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">📅 Created Date</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">⏳ Expiry Date</th>
              <th className="border border-gray-300 dark:border-gray-600 p-3 text-center">{t('obu.table_headers.management')}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedDevices.length === 0 ? (
              <tr>
                <td colSpan="11" className="text-center p-8 text-gray-500 dark:text-gray-400">
                  {t('obu.no_devices_found')}
                </td>
              </tr>
            ) : (
              paginatedDevices.map(device => (
                <tr key={device.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="border border-gray-300 dark:border-gray-600 p-3 font-mono font-medium">
                    {device.device_id}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3">
                    <div>
                      <div className="font-medium">{device.serial_number || '-'}</div>
                      {device.manufacturer && (
                        <div className="text-sm text-gray-500">{device.manufacturer}</div>
                      )}
                    </div>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <span className="inline-block px-2 py-1 rounded text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                      {device.location || '-'}
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3">
                    {device.license_plate ? (
                      <div>
                        <span className="inline-block px-2 py-1 bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 rounded font-medium">
                          🚗 {device.license_plate}
                        </span>
                        {device.vehicle_owner && (
                          <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{device.vehicle_owner}</div>
                        )}
                      </div>
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <span className="inline-block px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs font-medium">
                      {device.device_type}
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <span className="font-medium text-green-600">
                      💰 {device.balance ? parseFloat(device.balance).toLocaleString() : '0'} LAK
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <span className="font-medium text-orange-600">
                      {device.deposit_amount ? parseFloat(device.deposit_amount).toLocaleString() : '0'} LAK
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${deviceStatuses.find(s => s.value === device.status)?.color || 'bg-gray-100 text-gray-800'
                      }`}>
                      {deviceStatuses.find(s => s.value === device.status)?.label || device.status}
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    <span className="text-sm text-gray-600 dark:text-gray-300">
                      {device.created_at ? new Date(device.created_at).toLocaleDateString('en-GB') : '-'}
                    </span>
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3 text-center">
                    {device.warranty_expiry ? (
                      (() => {
                        const expiry = new Date(device.warranty_expiry);
                        const now = new Date();
                        const daysLeft = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
                        const isExpired = daysLeft < 0;
                        const isWarning = daysLeft >= 0 && daysLeft <= 30;
                        return (
                          <span className={`text-sm font-medium ${
                            isExpired ? 'text-red-600 dark:text-red-400' :
                            isWarning ? 'text-orange-500 dark:text-orange-400' :
                            'text-green-600 dark:text-green-400'
                          }`}>
                            {isExpired ? '⚠️' : isWarning ? '🟡' : '✅'}{' '}
                            {expiry.toLocaleDateString('en-GB')}
                            {isExpired && <div className="text-xs text-red-500">Expired</div>}
                            {isWarning && !isExpired && <div className="text-xs text-orange-400">{daysLeft}d left</div>}
                          </span>
                        );
                      })()
                    ) : (
                      <span className="text-gray-400 text-sm">-</span>
                    )}
                  </td>
                  <td className="border border-gray-300 dark:border-gray-600 p-3">
                    <div className="flex justify-center space-x-2">
                      <button
                        onClick={() => handleEdit(device)}
                        className="bg-blue-500 hover:bg-blue-600 text-white px-3 py-1 rounded text-xs transition-colors duration-200"
                        title={t('obu.tooltips.edit_device')}
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => handleDelete(device.id, device.device_id)}
                        className="bg-red-500 hover:bg-red-600 text-white px-3 py-1 rounded text-xs transition-colors duration-200"
                        title={t('obu.tooltips.delete_device')}
                      >
                        🗑️
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <TablePagination
        currentPage={safePage}
        totalItems={filteredDevices.length}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        itemLabel="devices"
      />
    </div>
  );
}
